import { useNavigate, useParams } from "react-router-dom";

import TableLoad from "components/load/TableLoad";
import PageError from "components/page-error";
import { PageTitle, PageLayout, ContentContainer } from "components/layout";
import { toast } from "components/ui/use-toast";

import {
  useGetRegionalSubstitutionByIdQuery,
  useUpdateRegionalSubstitutionMutation,
} from "@/services/regional-substitution";
import { SubstitutionForm } from "../components/substitution-form";
import { RegionalSubstitutionFormData } from "../validations/regional-substitution.validation";
import { REGIONAL_SUBSTITUTION_MESSAGES } from "../constants/messages";
import { REGIONAL_SUBSTITUTION_ROUTES } from "../constants/routes";
import { EDITABLE_STATUSES } from "../constants/status";
import {
  toDateInputValue,
  toIsoEndOfDay,
  toIsoStartOfDay,
} from "@/utils/date-vigencia";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { isConflictError } from "@/utils/error-mapper";

export const EditRegionalSubstitution: React.FC = () => {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } =
    useGetRegionalSubstitutionByIdQuery(id, { skip: !id });
  const [updateRegionalSubstitution, { isLoading: isSaving }] =
    useUpdateRegionalSubstitutionMutation();

  const goToList = () => navigate(REGIONAL_SUBSTITUTION_ROUTES.LIST);

  if (isLoading) {
    return (
      <PageLayout>
        <ContentContainer>
          <TableLoad />
        </ContentContainer>
      </PageLayout>
    );
  }

  if (error || !data) {
    return (
      <PageError
        status={500}
        message="Não foi possível carregar o vínculo. Tente novamente mais tarde."
      />
    );
  }

  if (!EDITABLE_STATUSES.includes(data.status)) {
    return (
      <PageError
        status={403}
        message="Este vínculo não pode ser editado. Apenas vínculos agendados permitem alteração de vigência."
      />
    );
  }

  const handleSubmit = async (form: RegionalSubstitutionFormData) => {
    try {
      await updateRegionalSubstitution({
        id,
        data: {
          version: data.version,
          initial_term: toIsoStartOfDay(form.initial_term),
          final_term: toIsoEndOfDay(form.final_term),
        },
      }).unwrap();
      toast(REGIONAL_SUBSTITUTION_MESSAGES.UPDATE.SUCCESS);
      goToList();
    } catch (submitError) {
      if (isConflictError(submitError)) {
        void refetch();
        toast(REGIONAL_SUBSTITUTION_MESSAGES.UPDATE.CONFLICT);
        return;
      }
      const apiMessage = extractRtkErrorMessage(
        submitError,
        REGIONAL_SUBSTITUTION_MESSAGES.UPDATE.ERROR.description,
      );
      toast({
        ...REGIONAL_SUBSTITUTION_MESSAGES.UPDATE.ERROR,
        description: apiMessage,
      });
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Editar Vigência da Substituição"
        description="Altere as datas de início e fim. Regional e Gerente não podem ser alterados."
      />
      <ContentContainer>
        <SubstitutionForm
          key={data.version}
          defaultValues={{
            regional_id: data.regional.id,
            user_id: data.user.id,
            initial_term: toDateInputValue(data.initial_term),
            final_term: toDateInputValue(data.final_term),
          }}
          peopleDisabled={true}
          submitting={isSaving}
          submitLabel="Salvar alterações"
          onSubmit={handleSubmit}
          onCancel={goToList}
          gerenteFallback={data.user}
        />
      </ContentContainer>
    </PageLayout>
  );
};
